import React, { useState, useEffect, useRef } from 'react';
import {
  Image as ImageIcon,
  Edit3,
  Trash2,
  Eye,
  Upload,
  Save,
  Plus,
  X,
  Check,
  Lightbulb,
  Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/Label';
import { Textarea } from './ui/Textarea';
import { Toast } from './ui/Toast';
import ConfirmDialog from './ConfirmDialog';
import useActualitesParams from '../hooks/useActualitesParams';
import { getAuthHeaders } from '../services/api';
import EnTeteParametrage from './parametrage/EnTeteParametrage';
import EnTeteSection from './parametrage/EnTeteSection';
import CarteReglages from './parametrage/CarteReglages';
import ChargementSection from './parametrage/ChargementSection';

/**
 * Paramétrage des actualités de la page d'accueil.
 *
 * ----------------------------------------------------------------------------
 * C'était un modal déguisé en page
 * ----------------------------------------------------------------------------
 * `ParametresActualitesModal` était rendu avec `isOpen={true}` sous un faux
 * en-tête écrit dans `ParametrageApp` : deux en-têtes empilés, un voile noir
 * plein écran sur une page qui n'en est pas une, et un bouton « Fermer » en
 * pied alors que le retour était déjà en haut.
 *
 * C'est désormais une page, comme les autres écrans de paramétrage, et elle
 * suit PARAMETRAGE-MODULES.md.
 *
 * Autres défauts corrigés :
 *
 * - **Cinq `alert()`** figeaient le navigateur et sortaient du système de
 *   messages de l'application. Ils passent par `Toast`.
 * - **`window.confirm` pour la suppression** : remplacé par `ConfirmDialog`,
 *   celui que le reste de l'application utilise.
 * - **L'échec de chargement s'affichait dans un bandeau rouge au milieu de la
 *   liste**, sous un titre qui annonçait « 0 actualité » — on ne savait pas si
 *   l'école n'en avait aucune ou si la lecture avait échoué. La liste dit
 *   maintenant laquelle des deux.
 */

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='200'%3E%3Crect width='400' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='system-ui, sans-serif' font-size='18' fill='%239ca3af'%3EImage non disponible%3C/text%3E%3C/svg%3E";

const ACCENT = '#246990';
const ACCENT_SOMBRE = '#1a4f6e';

const FORMULAIRE_VIDE = {
  titre: '',
  description: '',
  image_url: '',
  actif: true,
  couleur_fond: '#246990',
  couleur_texte: '#ffffff',
};

const ParametresActualites = ({ onBackToDashboard }) => {
  const {
    actualites,
    addActualite,
    updateActualite,
    deleteActualite,
    loadActualites,
    loading,
    error,
  } = useActualitesParams();

  const [form, setForm] = useState(FORMULAIRE_VIDE);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [aSupprimer, setASupprimer] = useState(null);
  const [suppression, setSuppression] = useState(false);
  // Horodatage du dernier envoi d'image, pour ignorer un enregistrement qui
  // arriverait dans la foulee du selecteur de fichier.
  const imageAjouteeLe = useRef(0);

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const avertir = (message, type = 'error') => setToast({ show: true, message, type });
  const confirmer = (message) => setToast({ show: true, message, type: 'success' });

  useEffect(() => {
    loadActualites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reinitialiser = () => {
    setForm(FORMULAIRE_VIDE);
    setEditingId(null);
    setPreview('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      avertir('Veuillez sélectionner une image valide');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      avertir("L'image ne doit pas dépasser 5 Mo");
      return;
    }

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const headers = getAuthHeaders();
      delete headers['Content-Type'];

      const response = await fetch('/api/parametres/actualites/upload', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) throw new Error("Erreur lors de l'upload");

      const data = await response.json();
      const imageUrl = data.imageUrl || data.url;
      setForm((prev) => ({ ...prev, image_url: imageUrl }));
      setPreview(imageUrl);
      imageAjouteeLe.current = Date.now();
      confirmer('Image envoyée');
    } catch (err) {
      console.error('Erreur upload:', err);
      avertir("Erreur lors de l'envoi de l'image");
    } finally {
      setUploadingImage(false);
    }
  };

  // L'enregistrement ne part QUE d'un clic sur le bouton : l'ecran n'a plus de
  // <form>, donc plus de soumission implicite. Choisir une image ne peut plus
  // declencher l'enregistrement, ni par la touche « Entree » du selecteur de
  // fichier, ni par le second clic d'un double-clic qui retombe sur la page.
  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (uploadingImage) {
      avertir("L'image est en cours d'envoi, patientez un instant", 'info');
      return;
    }

    // Le selecteur de fichier du systeme se referme sur un clic ou une touche
    // qui peut retomber sur la page. Dans la seconde qui suit un envoi d'image,
    // on considere qu'un enregistrement n'a pas ete voulu — et on le dit,
    // plutot que de l'avaler en silence.
    if (Date.now() - imageAjouteeLe.current < 1000) {
      avertir("Image ajoutée. Cliquez sur « Ajouter l'actualité » pour enregistrer", 'info');
      return;
    }

    if (!form.titre.trim()) {
      avertir('Le titre est requis');
      return;
    }
    if (!form.image_url.trim()) {
      avertir('Veuillez ajouter une image');
      return;
    }

    setEnregistrement(true);
    try {
      const resultat = editingId
        ? await updateActualite(editingId, { ...form })
        : await addActualite({ ...form });

      // Le hook ne leve pas : il rend { success, error }. Sans cette
      // verification, un refus du serveur s'affichait comme un enregistrement
      // reussi — et l'actualite avait disparu au retour sur la page.
      if (!resultat?.success) {
        avertir(resultat?.error || "Erreur lors de l'enregistrement");
        return;
      }

      confirmer(editingId ? 'Actualité mise à jour' : 'Actualité ajoutée');
      reinitialiser();
    } catch (err) {
      avertir(err.message || "Erreur lors de l'enregistrement");
    } finally {
      setEnregistrement(false);
    }
  };

  const handleEdit = (actu) => {
    setEditingId(actu.id);
    setForm({
      titre: actu.titre || '',
      description: actu.description || '',
      image_url: actu.image_url || '',
      actif: actu.actif ?? true,
      couleur_fond: actu.couleur_fond || '#246990',
      couleur_texte: actu.couleur_texte || '#ffffff',
    });
    setPreview(actu.image_url);
  };

  const supprimer = async () => {
    if (!aSupprimer || suppression) return;
    setSuppression(true);
    try {
      const resultat = await deleteActualite(aSupprimer.id);
      if (!resultat?.success) {
        avertir(resultat?.error || 'Erreur lors de la suppression');
        return;
      }
      confirmer('Actualité supprimée');
      if (editingId === aSupprimer.id) reinitialiser();
      setASupprimer(null);
    } catch (err) {
      avertir(err.message || 'Erreur lors de la suppression');
    } finally {
      setSuppression(false);
    }
  };

  const enTete = (
    <EnTeteParametrage
      onRetour={onBackToDashboard}
      icone={ImageIcon}
      titre="Paramétrage des actualités"
      sousTitre="Diaporama d'images affiché sur la page d'accueil"
    />
  );

  const actives = actualites.filter((a) => a.actif).length;

  return (
    <div className="w-full">
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {enTete}

      <div className="bg-white rounded-xl shadow-[0_1px_4px_rgba(0,0,0,0.06)] border border-gray-200/80 p-6">
        <div className="space-y-5">
          <EnTeteSection
            icone={ImageIcon}
            titre="Actualités de la page d'accueil"
            sousTitre="Seules les actualités actives apparaissent dans le diaporama"
            accent={ACCENT}
            accentSombre={ACCENT_SOMBRE}
            actions={
              editingId && (
                <Button
                  variant="outline"
                  onClick={reinitialiser}
                  className="h-9 justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Annuler la modification
                </Button>
              )
            }
          />

          {/* ------------------------------------------------------------ */}
          {/* Formulaire                                                    */}
          {/* ------------------------------------------------------------ */}
          <CarteReglages
            titre={editingId ? "Modifier l'actualité" : 'Ajouter une actualité'}
            accent={form.couleur_fond || ACCENT}
          >
            {/* `onKeyDown` : sans lui, la touche « Entree » — celle qui valide
                le selecteur de fichier du systeme — soumet le formulaire toute
                seule. C'est ce qui declenchait « Ajouter l'actualite » au moment
                meme ou l'on choisissait une image. On enregistre par le bouton,
                pas par une touche qui appartenait a une autre fenetre. */}
            <div>
              <div className="grid gap-4 lg:grid-cols-2">
                {/* Colonne gauche : le contenu */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-gray-700">Titre *</Label>
                    <Input
                      value={form.titre}
                      onChange={(e) => setForm({ ...form, titre: e.target.value })}
                      placeholder="Journée portes ouvertes"
                      className="mt-1 h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-gray-700">Description</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Texte affiché sous l'image"
                      rows={3}
                      className="mt-1 resize-none rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-gray-700">Image *</Label>
                    <div className="mt-1 space-y-2">
                      <label
                        className={`flex h-9 cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-3 text-sm transition-colors ${
                          uploadingImage
                            ? 'border-gray-200 bg-gray-50 text-gray-400'
                            : 'border-gray-300 text-gray-600 hover:border-[#246990] hover:text-[#246990]'
                        }`}
                      >
                        <Upload size={15} />
                        {uploadingImage ? 'Envoi en cours…' : 'Choisir un fichier'}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                      </label>
                      <Input
                        value={form.image_url}
                        onChange={(e) => {
                          setForm({ ...form, image_url: e.target.value });
                          setPreview(e.target.value);
                        }}
                        placeholder="… ou coller une adresse d'image"
                        className="h-9 rounded-lg border-gray-200 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                      />
                      <p className="text-[11px] text-gray-400">
                        5 Mo au maximum. Formats image uniquement.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Colonne droite : l'apparence et l'aperçu */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-gray-700">Aperçu</Label>
                    <div className="mt-1 overflow-hidden rounded-lg border border-gray-200">
                      <div className="relative h-36 bg-gray-50">
                        {preview || form.image_url ? (
                          <img
                            src={preview || form.image_url}
                            alt="Aperçu de l'actualité"
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              e.target.src = PLACEHOLDER_IMAGE;
                            }}
                          />
                        ) : (
                          <div className="flex h-full flex-col items-center justify-center gap-1 text-gray-300">
                            <ImageIcon size={26} />
                            <span className="text-[11px]">Aucune image</span>
                          </div>
                        )}
                        <div
                          className="absolute bottom-0 left-0 right-0 px-3 py-2"
                          style={{
                            backgroundColor: form.couleur_fond,
                            color: form.couleur_texte,
                          }}
                        >
                          <p className="truncate text-sm font-semibold">
                            {form.titre || 'Titre de l’actualité'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {[
                      ['couleur_fond', 'Fond du bandeau'],
                      ['couleur_texte', 'Texte du bandeau'],
                    ].map(([champ, libelle]) => (
                      <div key={champ}>
                        <Label className="text-sm text-gray-700">{libelle}</Label>
                        <div className="mt-1 flex items-center gap-2">
                          <input
                            type="color"
                            value={form[champ]}
                            onChange={(e) => setForm({ ...form, [champ]: e.target.value })}
                            className="h-9 w-10 flex-shrink-0 cursor-pointer rounded-lg border border-gray-200 bg-white p-1"
                            aria-label={libelle}
                          />
                          <Input
                            value={form[champ]}
                            onChange={(e) => setForm({ ...form, [champ]: e.target.value })}
                            placeholder="#246990"
                            className="h-9 rounded-lg border-gray-200 font-mono text-xs focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* L'interrupteur de la norme : dimensions figées. */}
                  <div className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-gray-800">
                        Afficher dans le diaporama
                      </p>
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        Une actualité inactive reste enregistrée mais n'apparaît pas.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, actif: !form.actif })}
                      aria-pressed={form.actif}
                      aria-label="Afficher dans le diaporama"
                      className="relative h-6 w-11 flex-shrink-0 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2"
                      style={{ backgroundColor: form.actif ? ACCENT : '#d1d5db' }}
                    >
                      <div
                        className={`absolute top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow-sm transition-transform duration-300 ${
                          form.actif ? 'translate-x-[22px]' : 'translate-x-[3px]'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
                {/* Le bouton reste hors service pendant l'envoi de l'image —
                    il n'y a rien a enregistrer tant qu'elle n'est pas arrivee.
                    Mais il DIT pourquoi : un bouton qui grisonne sans raison
                    donne l'impression d'agir tout seul, ce qui est exactement
                    ce qu'on cherchait a faire disparaitre. */}
                <Button
                  type="button"
                  onClick={handleSubmit}
                  disabled={enregistrement || uploadingImage}
                  className="h-9 min-w-[168px] justify-center gap-2 bg-[#246990] hover:bg-[#1f5a7a] text-white disabled:bg-gray-300"
                >
                  {uploadingImage ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : editingId ? (
                    <Save className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  {uploadingImage
                    ? "Envoi de l'image…"
                    : enregistrement
                      ? 'Enregistrement…'
                      : editingId
                        ? 'Enregistrer les modifications'
                        : "Ajouter l'actualité"}
                </Button>

                {editingId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={reinitialiser}
                    className="h-9 min-w-[168px] justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Annuler
                  </Button>
                )}
              </div>
            </div>
          </CarteReglages>

          {/* ------------------------------------------------------------ */}
          {/* Liste                                                         */}
          {/* ------------------------------------------------------------ */}
          <CarteReglages
            titre="Actualités enregistrées"
            accent={ACCENT}
            compteur={
              actualites.length === 0
                ? null
                : `${actives} active${actives > 1 ? 's' : ''} sur ${actualites.length}`
            }
          >
            {loading ? (
              <ChargementSection accent={ACCENT} libelle="Chargement des actualités…" />
            ) : error ? (
              /* Une lecture qui échoue ne s'affiche pas comme une liste vide :
                 le bandeau rouge apparaissait sous un titre annonçant « 0
                 actualité », et l'on ne savait pas laquelle des deux était
                 vraie. */
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-sm font-semibold text-red-800">
                  Les actualités n'ont pas pu être lues
                </p>
                <p className="mt-1 text-xs text-red-700">{error}</p>
                <Button
                  variant="outline"
                  onClick={loadActualites}
                  className="mt-3 h-9 justify-center gap-2"
                >
                  Réessayer
                </Button>
              </div>
            ) : actualites.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-10">
                <ImageIcon size={28} className="text-gray-300" />
                <p className="text-sm text-gray-500">Aucune actualité enregistrée</p>
                <p className="text-[11px] text-gray-400">
                  Le diaporama de la page d'accueil reste vide tant qu'aucune n'est active.
                </p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {actualites.map((a) => (
                  <div
                    key={a.id}
                    className={`group overflow-hidden rounded-lg border transition-colors ${
                      editingId === a.id
                        ? 'border-[#246990] ring-2 ring-[#246990]/15'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="relative h-32 bg-gray-50">
                      <img
                        src={a.image_url}
                        alt={a.titre}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.target.src = PLACEHOLDER_IMAGE;
                        }}
                      />
                      <div
                        className="absolute bottom-0 left-0 right-0 px-2 py-1.5"
                        style={{
                          backgroundColor: a.couleur_fond || ACCENT,
                          color: a.couleur_texte || '#ffffff',
                        }}
                      >
                        <p className="truncate text-sm font-semibold">{a.titre}</p>
                      </div>
                    </div>

                    <div className="p-3">
                      <p className="line-clamp-2 min-h-[32px] text-[11px] leading-relaxed text-gray-500">
                        {a.description || 'Pas de description'}
                      </p>

                      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleEdit(a)}
                            aria-label={`Modifier ${a.titre}`}
                            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-[#246990]"
                          >
                            <Edit3 size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setASupprimer(a)}
                            aria-label={`Supprimer ${a.titre}`}
                            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={15} />
                          </button>
                          <button
                            type="button"
                            onClick={() => window.open(a.image_url, '_blank', 'noopener')}
                            aria-label={`Voir l'image de ${a.titre}`}
                            className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
                          >
                            <Eye size={15} />
                          </button>
                        </div>

                        {a.actif ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700">
                            <Check size={13} /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-400">
                            <X size={13} /> Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CarteReglages>

          <p className="flex items-start gap-2 text-xs text-gray-500">
            <Lightbulb size={14} className="mt-0.5 flex-shrink-0 text-gray-400" />
            Les actualités actives apparaissent dans le diaporama de la page d'accueil,
            dans leur ordre d'enregistrement.
          </p>
        </div>
      </div>

      <ConfirmDialog
        isOpen={Boolean(aSupprimer)}
        onClose={() => { if (!suppression) setASupprimer(null); }}
        onConfirm={supprimer}
        isLoading={suppression}
        title="Supprimer l'actualité"
        description={`Êtes-vous sûr de vouloir supprimer « ${aSupprimer?.titre} » ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="warning"
      />
    </div>
  );
};

export default ParametresActualites;
